import { useSelector } from "react-redux";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions.ts";
import { closedTradesFilters } from "@shared/slices/filters.slice.ts";
import { serializeFilters } from "@shared/features/filters/utils.tsx";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid.tsx";
import { LanguageNameSpaces, TradeGraphType } from "@shared/types";
import React, { useMemo, useState } from "react";
import useMeasure from "utils/hooks/useMeasure.ts";
import TradesGraphCard from "features/trades/TradesCharts/TradesGraphCard.tsx";
import TradesGraphTabs from "features/trades/TradesCharts/TradesGraphTabs.tsx";
import styled, { useTheme } from "styled-components";
import TradesPieChart from "features/trades/TradesCharts/TradesPieChart.tsx";
import NumOfTradesChart from "features/trades/TradesCharts/NumOfTradesChart.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_LAYOUT_ONE_COLUMN } from "common/Layouts/TwoChartsTableLayout.tsx";
import StatsAndChartCard from "common/Boxes/StatsAndChartCard.tsx";
import useClosedTradesStatistics from "features/trades/ClosedTrades/useClosedTradesStatistics.tsx";
import {
  EXPANDED_CHART_HEIGHT,
  TABS_HEIGHT,
  TIME_RANGE_BUTTONS_HEIGHT,
} from "features/dashboard/MobileDashboard/ExpandingChart.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TradesStatisticsProps {
  isOpenTrades?: boolean;
  setChartFetching?: (chartFetching: boolean) => void;
  /** New look only: an action shown in the mobile allocation card header. */
  headerAction?: React.ReactNode;
}

const TradesCharts = ({
  isOpenTrades,
  setChartFetching,
  headerAction,
}: TradesStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const theme = useTheme();
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_LAYOUT_ONE_COLUMN})`],
    [true],
    false
  );
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const filtersSliceState = useSelector(
    closedTradesFilters.selectors.selectFilters
  );
  const filters = useMemo(
    () => serializeFilters(filtersSliceState),
    [filtersSliceState]
  );
  const useTabs = width < 700;
  const { closedTradesStatistic, totalChangeStatistic } =
    useClosedTradesStatistics();
  const [expanded, setExpanded] = useState(false);

  const getCharts = () => {
    if (isOpenTrades) return <NumOfTradesChart />;
    else if (!useTabs) {
      return (
        <EqualColumnsGrid numColumns={2} style={{ flex: "auto" }}>
          <TradesGraphCard
            title={t("charts.accumulated_profit")}
            graphType={TradeGraphType.ACCUMULATING_PROFIT}
            precision={precision}
            filters={filters}
            setChartFetching={setChartFetching}
          />
          <TradesGraphCard
            title={t("charts.profit_by_day")}
            graphType={TradeGraphType.PROFIT_BY_DAY}
            precision={precision}
            filters={filters}
            useBarChart={true}
            setChartFetching={setChartFetching}
          />
        </EqualColumnsGrid>
      );
    } else {
      return (
        <TradesGraphTabs
          expanded={expanded}
          filters={filters}
          precision={precision}
          setChartFetching={setChartFetching}
        />
      );
    }
  };

  if (isMobile) {
    if (isOpenTrades) {
      return (
        <div
          style={{
            marginBottom: theme.isNewLook ? 4 : 16,
            position: "relative",
          }}
        >
          <TradesPieChart headerAction={headerAction} />
        </div>
      );
    }
    return (
      <StatsAndChartCard
        expanded={expanded}
        setExpanded={setExpanded}
        topStatistic={totalChangeStatistic}
        bottomStatistic={closedTradesStatistic}
        chart={getCharts()}
        collapsedHeight={100}
        expandedHeight={
          EXPANDED_CHART_HEIGHT + TIME_RANGE_BUTTONS_HEIGHT + TABS_HEIGHT + 16
        }
      />
    );
  }

  return <Wrapper ref={measureRef}>{getCharts()}</Wrapper>;
};

export default React.memo(TradesCharts);

const Wrapper = styled.div`
  display: flex;
  column-gap: 16px;
  height: 100%;
`;
